// src/redux/services/store/storeShippingApi.ts
import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import { baseApi } from "../baseApi";

export type DecimalValue = number | string;

export type StoreCountry = {
  id: number;
  iso2_code: string;
  iso3_code?: string | null;
  name: string;
  phone_code?: string | null;
  currency_code?: string | null;
  is_active: boolean;
  sort_order: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type StoreDeliveryType = {
  id: number;
  code: string;
  name: string;
  is_active: boolean;
  sort_order: number;
  created_at?: string | null;
};

export type StoreShippingOptionsRequest = {
  country_code: string;
  state_name?: string | null;
  total_qty?: DecimalValue;
  total_weight_kg?: DecimalValue;
  subtotal_amount?: DecimalValue;
};

export type StoreShippingOptionRow = {
  delivery_type_id: number;
  delivery_type_code: string;
  delivery_type_name: string;
  shipping_amount: DecimalValue;
  currency_code: string;
  matched_rule_id: number;
  matched_rule_name: string;
  calc_basis: "flat" | "per_pcs" | "per_kg" | "tiered_pcs";
  matched_scope: string;
};

export type StoreShippingCalculateRequest = {
  country_code: string;
  state_name?: string | null;
  delivery_type_id: number;
  total_qty?: DecimalValue;
  total_weight_kg?: DecimalValue;
  subtotal_amount?: DecimalValue;
};

export type StoreShippingCalculation = {
  delivery_type_id: number;
  delivery_type_code?: string | null;
  delivery_type_name?: string | null;

  shipping_amount: DecimalValue;
  currency_code: string;

  matched_rule_id: number;
  matched_rule_name: string;
  calc_basis: "flat" | "per_pcs" | "per_kg" | "tiered_pcs";
  matched_country_code: string;
  matched_state_name?: string | null;
  matched_scope: string;

  total_qty: DecimalValue;
  total_weight_kg: DecimalValue;
  subtotal_amount: DecimalValue;
};

type Builder = EndpointBuilder<any, any, any>;

export const storeShippingApi = baseApi.injectEndpoints({
  endpoints: (builder: Builder) => ({
    getStoreShippingCountries: builder.query<StoreCountry[], void>({
      query: () => ({
        url: `/store/shipping/countries`,
        method: "GET",
      }),
      providesTags: ["StoreShippingCountries"],
    }),

    getStoreShippingDeliveryTypes: builder.query<StoreDeliveryType[], void>({
      query: () => ({
        url: `/store/shipping/delivery-types`,
        method: "GET",
      }),
      providesTags: ["StoreShippingDeliveryTypes"],
    }),

    getStoreShippingOptions: builder.mutation<
      StoreShippingOptionRow[],
      StoreShippingOptionsRequest
    >({
      query: (body) => ({
        url: `/store/shipping/options`,
        method: "POST",
        body: {
          country_code: body.country_code,
          state_name: body.state_name ?? null,
          total_qty: body.total_qty ?? 0,
          total_weight_kg: body.total_weight_kg ?? 0,
          subtotal_amount: body.subtotal_amount ?? 0,
        },
      }),
    }),

    calculateStoreShipping: builder.mutation<
      StoreShippingCalculation,
      StoreShippingCalculateRequest
    >({
      query: (body) => ({
        url: `/store/shipping/calculate`,
        method: "POST",
        body: {
          country_code: body.country_code,
          state_name: body.state_name ?? null,
          delivery_type_id: body.delivery_type_id,
          total_qty: body.total_qty ?? 0,
          total_weight_kg: body.total_weight_kg ?? 0,
          subtotal_amount: body.subtotal_amount ?? 0,
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetStoreShippingCountriesQuery,
  useGetStoreShippingDeliveryTypesQuery,
  useGetStoreShippingOptionsMutation,
  useCalculateStoreShippingMutation,
} = storeShippingApi;